import { useEffect, useState } from 'react'
import { teachersApi } from '@/api/endpoints/teachers'
import { adminsApi } from '@/api/endpoints/admins'

interface Person { id: string; name: string; isAdmin: boolean }

/**
 * Checkbox list of the parish's teachers (and admins, who can also teach — shown as "Admin-teacher").
 * Admin-only: the backend rejects teacher_ids from anyone else.
 */
export default function TeacherPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const [people, setPeople] = useState<Person[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const [teachers, admins] = await Promise.all([
          teachersApi.list().catch(() => []),
          adminsApi.list().catch(() => []),
        ])
        const fmt = (u: any) => [u.title, u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
        const list: Person[] = [
          ...(teachers as any[]).map(u => ({ id: u.id, name: fmt(u), isAdmin: false })),
          ...(admins as any[]).map(u => ({ id: u.id, name: fmt(u), isAdmin: true })),
        ]
        if (mounted) setPeople(list)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [])

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id])

  if (loading) return <div className="text-xs text-gray-500">Loading teachers…</div>
  if (!people.length) return <div className="text-xs text-gray-500">No teachers yet — add one in the Teachers section first.</div>
  return (
    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 p-2 space-y-1" title="Teachers assigned to a class can edit it and its calendar">
      {people.map(p => (
        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} />
          <span>{p.name}</span>
          {p.isAdmin && <span className="text-[10px] uppercase tracking-wide text-gray-500">Admin-teacher</span>}
        </label>
      ))}
    </div>
  )
}
